import {
  ReachCampaignDetails,
  SourcingChannelDetails,
} from 'api-clients/monolith';
import clsx from 'clsx';
import _isEmpty from 'lodash/isEmpty';
import _range from 'lodash/range';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import ChevronRightIcon from '@/assets/icons/chevron-sm-right.svg';
import { useConfirmation } from '@/components/base/confirmation';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useEditCampaignContext } from '@/contexts/edit-campaign-context';
import { formatShortDate } from '@/utils/date-util';

export function PublisherSelector() {
  const intl = useIntl();
  const { confirm } = useConfirmation();
  const {
    campaign,
    selectedPublisher,
    setSelectedPublisher,
    isFetching,
    hookForm: {
      formState: { dirtyFields },
    },
  } = useEditCampaignContext();

  const hasRepeatedChannels = useMemo(() => {
    const channelSlugs =
      campaign?.reach_campaigns?.map(p => p.sourcing_channel.slug) || [];
    return channelSlugs.length !== new Set(channelSlugs).size;
  }, [campaign?.reach_campaigns]);

  const toggleSelectedPublisher = async (publisher: ReachCampaignDetails) => {
    if (!_isEmpty(dirtyFields)) {
      const isConfirmed = await confirm({
        title: intl.formatMessage({
          defaultMessage: 'You have unsaved changes',
        }),
        description: intl.formatMessage({
          defaultMessage: 'Are you sure you want to switch publisher?',
        }),
        confirmLabel: intl.formatMessage({
          defaultMessage: 'Yes, switch',
        }),
        cancelLabel: intl.formatMessage({
          defaultMessage: 'No, cancel',
        }),
      });

      if (!isConfirmed) return;
    }

    setSelectedPublisher(publisher);
  };

  if (isFetching)
    return (
      <>
        {_range(3).map(i => (
          <MenuItem.Skeleton key={i} />
        ))}
      </>
    );

  return (
    <>
      {campaign?.reach_campaigns?.map(publisher => (
        <MenuItem
          key={publisher.reach_campaign_id}
          channel={publisher.sourcing_channel}
          isSelected={
            selectedPublisher?.reach_campaign_id === publisher.reach_campaign_id
          }
          toggleSelectedPublisher={() =>
            void toggleSelectedPublisher(publisher)
          }
          startDate={formatShortDate(publisher.starts_at)}
          endDate={publisher.end_at ? formatShortDate(publisher.end_at) : 'N/A'}
          hasRepeatedChannels={hasRepeatedChannels}
        />
      ))}
    </>
  );
}

function MenuItem({
  channel,
  isSelected,
  toggleSelectedPublisher,
  startDate,
  endDate,
  hasRepeatedChannels,
}: {
  channel: SourcingChannelDetails;
  isSelected: boolean;
  toggleSelectedPublisher: () => void;
  startDate: string;
  endDate: string;
  hasRepeatedChannels: boolean;
}) {
  const { slug, name } = channel;

  return (
    <button
      className={clsx(
        'hover:bg-fx-neutral-100 flex items-center gap-2 rounded px-2 py-1.5',
        isSelected && 'bg-fx-brand-50',
      )}
      type="button"
      onClick={toggleSelectedPublisher}
    >
      <ChannelIcon channelSlug={slug} variant="small" className="!h-4 !w-4" />
      <Typography
        variant="body1"
        className={clsx('flex items-start', !hasRepeatedChannels && 'flex-1')}
      >
        {name}
      </Typography>

      {/* Show start and end date only if the same channel has multiple reach campaigns */}
      {hasRepeatedChannels && (
        <Typography
          variant="small1"
          className="!text-fx-neutral-600 flex flex-1 items-start"
        >
          ({startDate} - {endDate})
        </Typography>
      )}
      <img src={ChevronRightIcon} alt="" />
    </button>
  );
}

MenuItem.Skeleton = function MenuItemSkeleton() {
  return (
    <div className="flex h-8 items-center gap-2 rounded px-2 py-1.5">
      <Skeleton className="h-5 w-5" />
      <Skeleton className="flex h-4 w-20 flex-1 items-start" />
      <img src={ChevronRightIcon} alt="" />
    </div>
  );
};
